import React, { useState, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import AddCircle from '@mui/icons-material/AddCircle';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import CircularProgress from '@mui/material/CircularProgress';
import { DateTime } from 'luxon';

import { createSearchParams, useSearchParams, useNavigate } from 'react-router-dom';
import { objectToQueryString, baseUrl, apiHelper } from 'helpers/util';
import { notify } from 'helpers/globalFunctions';

import ColumnBox from 'components/ColumnBox';
import SmallHeading from 'components/SmallHeading';
import StoreMenu from 'modules/StoreManager/StoreMenu'

export default function Form(props) {
    const entryType = props.entryType;
    const warehouse = props.warehouse;

    const navigate = useNavigate();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ storeEntryId, setStoreEntryId ] = useState(null);
    const [ entryDate, setEntryDate ] = useState(DateTime.now());

    const [ quantity, setQuantity ] = useState('');
    const [ rate, setRate ] = useState('');

    const [ productOptions, setProductOptions ] = useState([]);
    const [ productHash, setProductHash ] = useState({});
    const [ storeProduct, setStoreProduct ] = useState(null);

    const [ menuOptions, setMenuOptions ] = useState([]);
    const [ menu, setMenu ] = useState(null);
    const [ menuHash, setMenuHash ] = useState({});
    const formErrorState = useRef({})

    const [ remarks, setRemarks ] = useState('');
    const [ isFormSubmitting, setIsFormSubmitting ] = useState(false);
    const [ isMenuDialogOpen, setIsMenuDialogOpen ] = useState(false);

    const onClickSave = function() {
        for (const f in formErrorState.current) {
            if (formErrorState.current[f]) {
                notify.error({ description: 'Please fill all details' });
                return;
            }
        }
        let eDtStr = entryDate;
        if (entryDate && entryDate.toFormat) {
            eDtStr = entryDate.toFormat('yyyy-MM-dd HH:mm:ss');
        }
        const payload = { 'entry_datetime': eDtStr,
            'product': storeProduct, 'entry_type': entryType,
            warehouse, menu, quantity, remarks, rate
        }

        const apiParams = { "doctype": "Store Entry" };
        storeEntryId && (apiParams["name"] = storeEntryId)

        setIsFormSubmitting(true);
        let apiUrl = `${baseUrl}/.netlify/functions/erp_api`;
        let apiPromise = null;
        if (storeEntryId) {
            apiPromise = apiHelper.put({ "url": apiUrl, "params": apiParams, "data": payload })
        } else {
            apiPromise = apiHelper.post({ "url": apiUrl, "params": apiParams, "data": payload })
        }
        apiPromise
        .then(response => {
            if (response.status) {
                fillData();
                setIsFormSubmitting(false);
                notify.success({ "description": "Entry successfully added !" })
            } else { console.log(response.json); setIsFormSubmitting(false);
                notify.error({ "description": JSON.stringify(response.json) })
            }
        })
    }

    const loadProductOptions = function() {
        const pFilters = [["warehouse", "=", warehouse]]
        if (entryType == 'STOCK IN') { pFilters.push(["is_vegetable", "=", false]) }
        const apiParams = {
            "doctype": "Store Product",
            "fields": JSON.stringify(["product_name", "name", "unit", "is_vegetable"]),
            "order_by": "product_name asc",
            "limit": 1000,
            "filters": JSON.stringify(pFilters)
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) {
                const productHash = {};
                setProductOptions(response.json.data.map(el => el.name));
                for (const r of response.json.data) { productHash[r.name] = r; }
                setProductHash(productHash);
            } else { console.log(response.json); }
        })
    }

    const loadMenuOptions = function() {
        if (!entryDate) { setMenuOptions([]) }
        const apiParams = {
            "doctype": "Store Menu",
            "fields": JSON.stringify(["name", "menu_type", "menu_date"]),
            "order_by": "menu_type asc",
            "limit": 1000,
            "filters": JSON.stringify([["menu_date", "=", entryDate.toFormat('yyyy-MM-dd')]])
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) {
                setMenuOptions(response.json.data.map(el => el.name));
                const menuHash = {};
                for (const r of response.json.data) { menuHash[r.name] = r; }
                setMenuHash(menuHash);
            } else { console.log(response.json); }
        })
    }

    const loadStoreEntryDetail = function(storeEntryId) {
        const apiParams = {
            "doctype": "Store Entry",
            "name": storeEntryId,
            "fields": JSON.stringify(["*"]),
            "limit": "100"
        }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) {
                fillData(response.json.data);
            } else {
                console.log(response.json);
            }
        })
    }

    const fillData = function(data) {
        setStoreEntryId(data ? data.name : null);

        setEntryDate(data ? DateTime.fromSQL(data.entry_datetime) : DateTime.now());
        setStoreProduct(data ? data.product : null);
        // setMenu(data ? data.menu : null);
        setQuantity(data ? data.quantity : '');
        setRate(data ? data.rate : '');
        setRemarks(data ? data.remarks : '');
    }

    const checkForFormErrors = function(field, isError) {
        formErrorState.current[field] = isError
        return isError;
    }
    const generateProductLabel = function(el) {
        if (productHash[el] && productHash[el]['product_name']) {
            return `${productHash[el]['product_name']}${productHash[el]['is_vegetable'] ? ' 🥬' : ''}`
        }
        return el;
    }
    const handleMenuDialogClose = function() {
        setIsMenuDialogOpen(false);
    }

    useEffect(function() {
        if (!isMenuDialogOpen) { loadMenuOptions(); }
    }, [entryDate, isMenuDialogOpen]);

    useEffect(function() {
        loadProductOptions();
        loadMenuOptions();
        if (searchParams.get('store_entry_id')) {
            loadStoreEntryDetail(searchParams.get('store_entry_id'));
        }
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center',
            padding: '15px' }}>
            <ColumnBox sx={{ alignItems: 'center', maxWidth: '500px',
                width: '100%' }}>
                <SmallHeading>{entryType}</SmallHeading>
                <Box sx={{ marginTop: '15px', width: '100%' }}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <MobileDateTimePicker
                            onChange={(newValue) => { setEntryDate(newValue); }}
                            value={entryDate}
                            inputFormat="dd-MMM-yyyy hh:mm a"
                            closeOnSelect={true} disableMaskedInput={true}
                            renderInput={(params) => <TextField {...params} required
                                error={checkForFormErrors('entry_datetime', !entryDate)}
                                label="Entry Date" size="small" sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Box>
                { entryType == 'STOCK OUT' &&
                    <ColumnBox sx={{ width: '100%', marginTop: '15px' }}>
                        {/* <Autocomplete
                            disablePortal
                            size="small"
                            onChange={(e, newValue) => { setMenu(newValue); }}
                            options={menuOptions}
                            value={menu}
                            getOptionLabel={el => (menuHash[el] && menuHash[el]['menu_type'])
                                ? menuHash[el]['menu_type'] : el}
                            renderInput={(params) => <TextField required
                                error={checkForFormErrors('menu', !menu)}
                                {...params} label="Menu" />}
                        /> */}
                        {/* <Box sx={{ alignSelf: 'flex-end', color: '#1976d2',
                            cursor: 'pointer' }} onClick={ () => setIsMenuDialogOpen(true) }
                            >Add new ?</Box> */}
                    </ColumnBox>
                }
                <Autocomplete
                    disablePortal
                    size="small"
                    onChange={(e, newValue) => { setStoreProduct(newValue); }}
                    options={productOptions}
                    value={storeProduct}
                    sx={{ width: '100%', marginTop: '15px' }}
                    getOptionLabel={el => generateProductLabel(el)}
                    renderInput={(params) => <TextField required
                        error={checkForFormErrors('product', !storeProduct)}
                        {...params} label="Product" />}
                />
                <TextField label={"Quantity" + (productHash[storeProduct] ?
                    ` (in ${productHash[storeProduct]['unit']})` : '')}
                    onChange={(e) => setQuantity(e.target.value)}
                    value={quantity} required
                    error={checkForFormErrors('quantity', quantity === '' || quantity === null)}
                    sx={{ width: '100%', marginTop: '15px' }}
                    type="number"
                    size="small"
                />
                {productHash[storeProduct] && productHash[storeProduct]['is_vegetable'] &&
                    <TextField label={"Rate"}
                        onChange={(e) => setRate(e.target.value)}
                        value={rate} required
                        error={checkForFormErrors('rate', rate === '' || rate === null)}
                        sx={{ width: '100%', marginTop: '15px' }}
                        type="number"
                        size="small"
                    />
                }
                <TextField label="Remarks"
                    sx={{ width: '100%', marginTop: '15px' }}
                    minRows={2}
                    onChange={(e) => { setRemarks(e.target.value); }}
                    value={remarks}
                    multiline={true} />

                { isFormSubmitting ? <CircularProgress sx={{ marginTop: '15px' }} /> :
                    <Button sx={{ marginTop: '15px' }} onClick={() => onClickSave()}
                        variant="outlined">Save</Button>
                }
            </ColumnBox>
            <Dialog open={isMenuDialogOpen} onClose={handleMenuDialogClose}>
                <DialogTitle>Add new Menu</DialogTitle>
                <DialogContent>
                    <StoreMenu />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleMenuDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
